import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
    PhotoIcon,
    PlusIcon,
    TrashIcon,
    PencilIcon,
    Bars3BottomLeftIcon,
    CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import { Award } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const baseUrl = API_URL ? (API_URL.endsWith('/api') ? API_URL.replace('/api', '') : API_URL) : '';

const LifeAtAhamRecognitionPage = () => {
    const [settings, setSettings] = useState({ section_label: '', section_title: '', description: '' });
    const [awards, setAwards] = useState([]);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showAwardModal, setShowAwardModal] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);

    const [currentAward, setCurrentAward] = useState({
        award_title: '', award_description: '', award_year: '', icon_name: 'Award', display_order: 0, is_active: true
    });
    const [currentImage, setCurrentImage] = useState({
        image_url: '', caption: '', overlay_text: '', display_order: 0, is_active: true
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    // Delete confirmation states
    const [deleteAwardConfirm, setDeleteAwardConfirm] = useState({ isOpen: false, id: null });
    const [deleteImageConfirm, setDeleteImageConfirm] = useState({ isOpen: false, id: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, awardsRes, imagesRes] = await Promise.all([
                axios.get('/api/admin/life-at-aham/recognition/settings'),
                axios.get('/api/admin/life-at-aham/recognition/awards'),
                axios.get('/api/admin/life-at-aham/recognition/images')
            ]);
            if (settingsRes.data.success) setSettings(settingsRes.data.data);
            if (awardsRes.data.success) setAwards(awardsRes.data.data);
            if (imagesRes.data.success) setImages(imagesRes.data.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load recognition data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleSaveSettings = async () => {
        try {
            setSaving(true);
            await axios.put('/api/admin/life-at-aham/recognition/settings', settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            toast.error('Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAward = async () => {
        try {
            if (currentAward.id) {
                await axios.put(`/api/admin/life-at-aham/recognition/awards/${currentAward.id}`, currentAward);
                toast.success('Award updated');
            } else {
                await axios.post('/api/admin/life-at-aham/recognition/awards', currentAward);
                toast.success('Award added');
            }
            setShowAwardModal(false);
            fetchData();
        } catch (error) {
            toast.error('Failed to save award');
        }
    };

    const handleDeleteAward = async () => {
        try {
            await axios.delete(`/api/admin/life-at-aham/recognition/awards/${deleteAwardConfirm.id}`);
            toast.success('Award deleted');
            setDeleteAwardConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            toast.error('Failed to delete award');
        }
    };

    const handleSaveImage = async () => {
        try {
            const submitData = new FormData();
            submitData.append('caption', currentImage.caption);
            submitData.append('overlay_text', currentImage.overlay_text);
            submitData.append('display_order', currentImage.display_order);
            submitData.append('is_active', currentImage.is_active ? '1' : '0');
            if (imageFile) submitData.append('image', imageFile);
            else submitData.append('image_url', currentImage.image_url);

            if (currentImage.id) {
                await axios.put(`/api/admin/life-at-aham/recognition/images/${currentImage.id}`, submitData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Image updated');
            } else {
                await axios.post('/api/admin/life-at-aham/recognition/images', submitData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Image added');
            }
            setShowImageModal(false);
            setImageFile(null);
            setImagePreview(null);
            fetchData();
        } catch (error) {
            console.error('Error saving image:', error);
            toast.error(error.response?.data?.message || 'Failed to save image');
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.match(/^image\/(webp|png)$/)) {
                toast.error('Only WebP and PNG formats are allowed');
                e.target.value = '';
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                toast.error('File size must be less than 5MB');
                e.target.value = '';
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageFile(file);
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDeleteImage = async () => {
        try {
            await axios.delete(`/api/admin/life-at-aham/recognition/images/${deleteImageConfirm.id}`);
            toast.success('Image deleted');
            setDeleteImageConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            toast.error('Failed to delete image');
        }
    };

    const resolveImageUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
    };

    return (
        <div className="space-y-8 pb-20">
            <div>
                <h1 className="text-3xl font-bold text-slate-800">"Recognition & Excellence" - Management</h1>
                <p className="text-slate-500 mt-1">Manage description, awards, and images for the "Recognition & Excellence" section</p>
            </div>

            {loading ? (
                <div className="animate-pulse space-y-4">
                    <div className="h-64 bg-slate-100 rounded-2xl"></div>
                    <div className="h-64 bg-slate-100 rounded-2xl"></div>
                </div>
            ) : (
                <>
                    {/* Settings */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                        <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                            <Bars3BottomLeftIcon className="h-5 w-5 text-primary-600" />
                            Section Content Settings
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Section Label</label>
                                <input
                                    type="text"
                                    value={settings.section_label}
                                    onChange={(e) => setSettings({ ...settings, section_label: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                    placeholder="e.g., Recognition & Excellence"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                                <input
                                    type="text"
                                    value={settings.section_title}
                                    onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                    placeholder="e.g., Our Awards & Achievements"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Main Description</label>
                                <textarea
                                    rows="3"
                                    value={settings.description}
                                    onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end mt-6">
                            <button
                                onClick={handleSaveSettings}
                                disabled={saving}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg disabled:opacity-50"
                            >
                                {saving ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>

                    {/* Images */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <PhotoIcon className="h-5 w-5 text-primary-600" />
                                Section Images & Captions
                            </h2>
                            <button
                                onClick={() => {
                                    setCurrentImage({ image_url: '', caption: '', overlay_text: '', display_order: images.length, is_active: true });
                                    setImagePreview(null);
                                    setImageFile(null);
                                    setShowImageModal(true);
                                }}
                                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold"
                            >
                                <PlusIcon className="h-5 w-5" /> Add New Image
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {images.map((img) => (
                                <div key={img.id} className="border border-slate-100 rounded-2xl overflow-hidden bg-slate-50 group relative">
                                    <img src={resolveImageUrl(img.image_url)} alt="Recognition" className="w-full h-48 object-cover" />
                                    <div className="p-4">
                                        <p className="text-sm text-slate-600 italic leading-relaxed">{img.caption}</p>
                                        {img.overlay_text && <p className="text-xs text-slate-500 mt-2">Overlay: {img.overlay_text}</p>}
                                    </div>
                                    <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => {
                                            setCurrentImage(img);
                                            setImagePreview(resolveImageUrl(img.image_url));
                                            setImageFile(null);
                                            setShowImageModal(true);
                                        }} className="p-2 bg-white text-blue-600 rounded-xl shadow-lg">
                                            <PencilIcon className="h-5 w-5" />
                                        </button>
                                        <button onClick={() => setDeleteImageConfirm({ isOpen: true, id: img.id })} className="p-2 bg-white text-red-600 rounded-xl shadow-lg">
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Awards */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <Award className="h-5 w-5 text-primary-600" />
                                Awards & Achievements
                            </h2>
                            <button
                                onClick={() => { setCurrentAward({ award_title: '', award_description: '', award_year: '', icon_name: 'Award', display_order: awards.length, is_active: true }); setShowAwardModal(true); }}
                                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold"
                            >
                                <PlusIcon className="h-5 w-5" /> Add New Award
                            </button>
                        </div>
                        <div className="space-y-4">
                            {awards.map((award) => (
                                <div key={award.id} className="p-4 border border-slate-100 rounded-2xl bg-slate-50 relative group">
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0">
                                            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                                                <Award className="h-6 w-6 text-primary-600" />
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-bold text-slate-800">{award.award_title}</h4>
                                            <p className="text-sm text-slate-600 mt-1">{award.award_description}</p>
                                            <div className="flex items-center gap-3 mt-2">
                                                <span className="text-xs font-semibold text-primary-600 bg-primary-50 px-2 py-1 rounded">{award.award_year}</span>
                                                <span className={`text-xs font-semibold px-2 py-1 rounded ${award.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                                    {award.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => { setCurrentAward(award); setShowAwardModal(true); }} className="p-1.5 bg-white text-blue-600 rounded-lg shadow-sm">
                                            <PencilIcon className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => setDeleteAwardConfirm({ isOpen: true, id: award.id })} className="p-1.5 bg-white text-red-600 rounded-lg shadow-sm">
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Award Modal */}
            {showAwardModal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl">
                        <h3 className="text-xl font-bold text-slate-800 mb-6">Manage Award</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Award Title</label>
                                <input
                                    type="text"
                                    value={currentAward.award_title}
                                    onChange={(e) => setCurrentAward({ ...currentAward, award_title: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
                                <textarea
                                    rows="3"
                                    value={currentAward.award_description}
                                    onChange={(e) => setCurrentAward({ ...currentAward, award_description: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Year</label>
                                    <input
                                        type="text"
                                        value={currentAward.award_year}
                                        onChange={(e) => setCurrentAward({ ...currentAward, award_year: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                        placeholder="2024"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Display Order</label>
                                    <input
                                        type="number"
                                        value={currentAward.display_order}
                                        onChange={(e) => setCurrentAward({ ...currentAward, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={currentAward.is_active}
                                        onChange={(e) => setCurrentAward({ ...currentAward, is_active: e.target.checked })}
                                        className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 border-gray-300"
                                    />
                                    <span className="text-sm font-semibold text-slate-700">Active Status</span>
                                </label>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-8">
                            <button onClick={() => setShowAwardModal(false)} className="px-6 py-2.5 text-slate-600 font-semibold">Cancel</button>
                            <button onClick={handleSaveAward} className="bg-primary-600 text-white px-8 py-2.5 rounded-xl font-semibold shadow-lg">Save Award</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Modal */}
            {showImageModal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-xl p-8 shadow-2xl">
                        <h3 className="text-xl font-bold text-slate-800 mb-6">Manage Image</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Image Selection</label>
                                <p className="text-xs text-slate-500 mb-3">📐 Recommended: 300 x 300 pixels | Formats: WebP, PNG only</p>
                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-primary-500 transition-colors">
                                            <input type="file" accept=".webp,.png" onChange={handleImageChange} className="hidden" />
                                            <CloudArrowUpIcon className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                                            <p className="text-sm font-medium text-slate-600">Change Image</p>
                                            <p className="text-xs text-slate-400 mt-1">WebP or PNG, max 5MB</p>
                                        </label>
                                    </div>
                                    {imagePreview && (
                                        <div className="w-32 h-24 rounded-xl overflow-hidden border border-slate-200">
                                            <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Caption</label>
                                <textarea
                                    rows="2"
                                    value={currentImage.caption}
                                    onChange={(e) => setCurrentImage({ ...currentImage, caption: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Overlay Text</label>
                                <input
                                    type="text"
                                    value={currentImage.overlay_text}
                                    onChange={(e) => setCurrentImage({ ...currentImage, overlay_text: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                    placeholder="e.g., Award Winning Team"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Display Order</label>
                                    <input
                                        type="number"
                                        value={currentImage.display_order}
                                        onChange={(e) => setCurrentImage({ ...currentImage, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Status</label>
                                    <label className="relative inline-flex items-center cursor-pointer mt-2">
                                        <input
                                            type="checkbox"
                                            checked={currentImage.is_active}
                                            onChange={(e) => setCurrentImage({ ...currentImage, is_active: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                        <span className="ml-3 text-sm font-medium text-slate-700">{currentImage.is_active ? 'Active' : 'Inactive'}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-8">
                            <button onClick={() => setShowImageModal(false)} className="px-6 py-2.5 text-slate-600 font-semibold">Cancel</button>
                            <button onClick={handleSaveImage} className="bg-primary-600 text-white px-8 py-2.5 rounded-xl font-semibold shadow-lg">Save Image</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Award Confirmation Dialog */}
            <ConfirmDialog
                isOpen={deleteAwardConfirm.isOpen}
                onClose={() => setDeleteAwardConfirm({ isOpen: false, id: null })}
                onConfirm={handleDeleteAward}
                title="Delete Award"
                message="Are you sure you want to delete this award? This action cannot be undone."
            />

            {/* Delete Image Confirmation Dialog */}
            <ConfirmDialog
                isOpen={deleteImageConfirm.isOpen}
                onClose={() => setDeleteImageConfirm({ isOpen: false, id: null })}
                onConfirm={handleDeleteImage}
                title="Delete Recognition Image"
                message="Are you sure you want to delete this image? This action cannot be undone."
            />
        </div>
    );
};

export default LifeAtAhamRecognitionPage;
